import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { Country, State } from "country-state-city";
import { AuthService } from "@/hooks/services/authService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { useAvailabilityCheck } from "@/hooks/useAvailabilityCheck";
import {
  Mail, Lock, User, School, Phone, ChevronDown, Search, X,
  ArrowRight, CheckCircle2, AlertCircle, Eye, EyeOff,
  Wand2, Loader2, CircleCheck, CircleX,
} from "lucide-react";

// ── Searchable combobox ───────────────────────────────────────────────────────
function SearchableSelect({ label, value, onChange, options, disabled, placeholder, badge }) {
  const [query, setQuery] = useState("");
  const [open, setOpen]   = useState(false);
  const containerRef      = useRef(null);
  const inputRef          = useRef(null);

  // Close on outside click
  useEffect(() => {
    function onDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [query, options]);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? "";

  const handleSelect = (opt) => {
    onChange(opt.value);
    setQuery("");
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setQuery("");
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    if (!open) setOpen(true);
  };

  const displayValue = open ? query : selectedLabel;

  return (
    <div className="space-y-2" ref={containerRef}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold text-slate-700">{label}</label>
        {badge}
      </div>
      <div className="relative">
        {/* Input */}
        <div
          className={`flex items-center w-full bg-white/70 border ${open ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-slate-200/80"} rounded-xl transition-all ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          onClick={() => { if (!disabled) { setOpen(true); inputRef.current?.focus(); } }}
        >
          <Search className="w-4 h-4 text-slate-400 ml-4 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            disabled={disabled}
            placeholder={placeholder}
            className="flex-1 bg-transparent px-3 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none disabled:cursor-not-allowed"
          />
          {value && !disabled && (
            <button type="button" onClick={handleClear} className="p-1 mr-1 text-slate-400 hover:text-slate-600 rounded transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-400 mr-3 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>

        {/* Dropdown */}
        {open && !disabled && (
          <ul className="absolute z-50 mt-1.5 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-slate-400">No results for "{query}"</li>
            ) : (
              filtered.map((opt) => (
                <li
                  key={opt.value}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
                  className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${opt.value === value ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}
                >
                  {opt.label}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Availability badge ────────────────────────────────────────────────────────
function AvailBadge({ status }) {
  if (status === "idle") return null;
  if (status === "checking") return (
    <span className="flex items-center gap-1 text-xs text-slate-400 font-medium">
      <Loader2 className="w-3 h-3 animate-spin" /> checking…
    </span>
  );
  if (status === "available") return (
    <span className="flex items-center gap-1 text-xs text-emerald-600 font-semibold">
      <CircleCheck className="w-3.5 h-3.5" /> available
    </span>
  );
  if (status === "taken") return (
    <span className="flex items-center gap-1 text-xs text-red-500 font-semibold">
      <CircleX className="w-3.5 h-3.5" /> already taken
    </span>
  );
  return <span className="text-xs text-amber-500 font-medium">could not verify</span>;
}

function fieldBorder(status) {
  if (status === "available") return "border-emerald-400 ring-2 ring-emerald-500/20";
  if (status === "taken")     return "border-red-400   ring-2 ring-red-400/20";
  return "border-slate-200/80";
}

// ── Code generator ─────────────────────────────────────────────────────────────
function generateCode(schoolName) {
  const words = (schoolName || "").trim().split(/\s+/).filter(Boolean);
  let prefix;
  if (words.length >= 2) {
    prefix = words.slice(0, 3).map((w) => w[0].toUpperCase()).join("");
    if (prefix.length < 3) prefix = (prefix + words[0].slice(1, 5).toUpperCase()).slice(0, 4);
  } else if (words.length === 1) {
    prefix = words[0].slice(0, 4).toUpperCase();
  } else {
    prefix = "SCH";
  }
  return `${prefix}-${Math.floor(100 + Math.random() * 900)}`;
}


// ── Main ─────────────────────────────────────────────────────────────────────
export default function SchoolSignUpPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    schoolName: "", schoolCode: "", street: "",
    principalName: "", principalPhone: "", phone: "",
    email: "", password: "", confirmPassword: "",
  });
  const [location, setLocation] = useState({
    countryCode: "", stateCode: "", city: "",
  });

  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);

  // Availability checks
  const emailCheck      = useAvailabilityCheck({ type: "email",       minLength: 6 });
  const codeCheck       = useAvailabilityCheck({ type: "school-code", minLength: 4 });
  const nameCheck       = useAvailabilityCheck({ type: "school-name", minLength: 6 });

  // Cascading data — build option lists for SearchableSelect
  const countryOptions = useMemo(
    () => Country.getAllCountries().map((c) => ({ value: c.isoCode, label: `${c.flag} ${c.name}` })),
    [],
  );
  const stateOptions = useMemo(
    () => location.countryCode
      ? State.getStatesOfCountry(location.countryCode).map((s) => ({ value: s.isoCode, label: s.name }))
      : [],
    [location.countryCode],
  );

  const selectedCountry = useMemo(
    () => Country.getAllCountries().find((c) => c.isoCode === location.countryCode),
    [location.countryCode],
  );
  const selectedState = useMemo(
    () => State.getStatesOfCountry(location.countryCode).find((s) => s.isoCode === location.stateCode),
    [location.countryCode, location.stateCode],
  );

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
    if (name === "email")      emailCheck.check(value);
    if (name === "schoolCode") codeCheck.check(value);
    if (name === "schoolName") nameCheck.check(value);
  }, [error, emailCheck, codeCheck, nameCheck]);

  const handleCountryChange = (val) => setLocation({ countryCode: val, stateCode: "", city: "" });
  const handleStateChange   = (val) => setLocation((prev) => ({ ...prev, stateCode: val, city: "" }));
  const handleCityChange    = (e) => setLocation((prev) => ({ ...prev, city: e.target.value }));

  const handleGenerateCode = () => {
    const code = generateCode(formData.schoolName);
    setFormData((prev) => ({ ...prev, schoolCode: code }));
    codeCheck.check(code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.schoolName || !formData.schoolCode || !formData.email || !formData.password || !formData.principalName) {
      setError("Please fill in all required fields"); return;
    }
    if (!formData.street)      { setError("Please enter the school street address"); return; }
    if (!formData.phone)       { setError("Please enter the school phone number"); return; }
    if (!formData.principalPhone) { setError("Please enter the principal's phone number"); return; }
    if (!location.countryCode) { setError("Please select a country"); return; }
    if (!location.stateCode)   { setError("Please select a state / region"); return; }
    if (formData.password !== formData.confirmPassword) { setError("Passwords do not match"); return; }
    if (formData.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (codeCheck.status  === "taken") { setError("That school code is already taken — generate a new one."); return; }
    if (emailCheck.status === "taken") { setError("That email is already registered."); return; }
    if (nameCheck.status  === "taken") { setError("A school with that name already exists."); return; }

    setIsLoading(true);
    try {
      const result = await AuthService.registerAdmin({
        name:           formData.principalName,
        email:          formData.email,
        password:       formData.password,
        schoolName:     formData.schoolName,
        schoolCode:     formData.schoolCode.toUpperCase(),
        principalName:  formData.principalName,
        principalEmail: formData.email,
        principalPhone: formData.principalPhone,
        phone:          formData.phone,
        address: {
          street:  formData.street,
          city:    location.city || selectedState?.name || "",
          state:   selectedState?.name || location.stateCode,
          country: selectedCountry?.name || location.countryCode,
        },
      });
      AuthStore.getState().login(true, "school_admin", result.user, result.accessToken, result.refreshToken);
      setSuccess(true);
      setTimeout(() => navigate("/school-dashboard"), 1500);
    } catch (err) {
      setError(err?.response?.data?.error?.message || err.message || "Registration failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const inputBase = "w-full pl-11 pr-4 py-3.5 bg-white/70 border rounded-xl focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm disabled:opacity-50";
  const pwBase    = "w-full pl-11 pr-11 py-3.5 bg-white/70 border border-slate-200/80 rounded-xl focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm disabled:opacity-50";

  const blocked = isLoading || success || codeCheck.status === "taken" || emailCheck.status === "taken" || nameCheck.status === "taken";

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/20 flex items-center justify-center p-6">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-16 left-8  w-80 h-80 bg-emerald-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-24 right-8 w-96 h-96 bg-teal-200/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-2xl space-y-7 py-10">
        {/* Portal breadcrumb */}
        <button onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium transition-colors">
          <School className="w-4 h-4" />
          School Portal
          <span className="text-slate-300">·</span>
          <span className="text-emerald-600 hover:underline">Change portal</span>
        </button>

        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-slate-900">Register your school</h1>
          <p className="text-slate-500 text-sm">Set up your school on the EarlyScape platform</p>
        </div>

        <div className="backdrop-blur-xl bg-white/50 border border-white/70 rounded-3xl p-8 sm:p-10 shadow-2xl">
          {success && (
            <div className="mb-6 p-4 bg-green-50/80 border border-green-200/50 rounded-xl flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
              <span className="text-green-800 font-medium text-sm">School registered! Redirecting…</span>
            </div>
          )}
          {error && !success && (
            <div className="mb-6 p-4 bg-red-50/80 border border-red-200/50 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
              <span className="text-red-800 font-medium text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-7">

            {/* ── School info ── */}
            <section className="space-y-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">School Information</p>

              {/* School name */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-slate-700">School Name</label>
                  <AvailBadge status={nameCheck.status} />
                </div>
                <div className="relative">
                  <School className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="text" name="schoolName" placeholder="Greenwood Academy"
                    value={formData.schoolName} onChange={handleChange} disabled={isLoading || success}
                    className={`${inputBase} ${fieldBorder(nameCheck.status)}`} />
                </div>
              </div>

              {/* School code + generate */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-slate-700">School Code</label>
                  <AvailBadge status={codeCheck.status} />
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <School className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" name="schoolCode" placeholder="e.g. GWA-042"
                      value={formData.schoolCode} onChange={handleChange} disabled={isLoading || success}
                      className={`${inputBase} ${fieldBorder(codeCheck.status)}`} />
                  </div>
                  <button type="button" onClick={handleGenerateCode} disabled={isLoading || success}
                    className="flex items-center gap-1.5 px-3.5 py-3 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-700 text-xs font-semibold transition-colors shrink-0 disabled:opacity-50">
                    <Wand2 className="w-3.5 h-3.5" /> Generate
                  </button>
                </div>
                <p className="text-xs text-slate-400">Students use this code when signing up.</p>
              </div>

              {/* Country → State → City */}
              <SearchableSelect
                label="Country"
                value={location.countryCode}
                onChange={handleCountryChange}
                options={countryOptions}
                disabled={isLoading || success}
                placeholder="Search country…"
              />

              <div className="grid grid-cols-2 gap-4">
                <SearchableSelect
                  label="State / Region"
                  value={location.stateCode}
                  onChange={handleStateChange}
                  options={stateOptions}
                  disabled={!location.countryCode || isLoading || success}
                  placeholder={location.countryCode ? "Search state…" : "Select country first"}
                />

                {/* City — free text */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">City / Area</label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={location.city} onChange={handleCityChange}
                      disabled={!location.stateCode || isLoading || success}
                      placeholder={location.stateCode ? "e.g. Victoria Island" : "Select state first"}
                      className="w-full pl-11 pr-4 py-3.5 bg-white/70 border border-slate-200/80 rounded-xl focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm disabled:opacity-50 disabled:cursor-not-allowed" />
                  </div>
                </div>
              </div>

              {/* Street address + school phone */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Street Address</label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" name="street" placeholder="12 Broad Street"
                      value={formData.street} onChange={handleChange} disabled={isLoading || success}
                      className={`${inputBase} border-slate-200/80`} />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">School Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="tel" name="phone" placeholder="+234 800 000 0000"
                      value={formData.phone} onChange={handleChange} disabled={isLoading || success}
                      className={`${inputBase} border-slate-200/80`} />
                  </div>
                </div>
              </div>
            </section>

            {/* ── Principal ── */}
            <section className="space-y-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Principal / Admin</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" name="principalName" placeholder="Dr. Jane Doe"
                      value={formData.principalName} onChange={handleChange} disabled={isLoading || success}
                      className={`${inputBase} border-slate-200/80`} />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="tel" name="principalPhone" placeholder="+234 800 000 0000"
                      value={formData.principalPhone} onChange={handleChange} disabled={isLoading || success}
                      className={`${inputBase} border-slate-200/80`} />
                  </div>
                </div>
              </div>
            </section>

            {/* ── Credentials ── */}
            <section className="space-y-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Login Credentials</p>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-slate-700">Admin Email</label>
                  <AvailBadge status={emailCheck.status} />
                </div>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="email" name="email" placeholder="admin@school.com"
                    value={formData.email} onChange={handleChange} disabled={isLoading || success}
                    className={`${inputBase} ${fieldBorder(emailCheck.status)}`} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type={showPassword ? "text" : "password"} name="password" placeholder="Min. 8 chars"
                      value={formData.password} onChange={handleChange} disabled={isLoading || success}
                      className={pwBase} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type={showConfirm ? "text" : "password"} name="confirmPassword" placeholder="Repeat password"
                      value={formData.confirmPassword} onChange={handleChange} disabled={isLoading || success}
                      className={pwBase} />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <button type="submit" disabled={blocked}
              className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-semibold text-sm shadow-lg transition-all disabled:opacity-50">
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Registering School…
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Register School <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200/60" />
            <span className="text-xs text-slate-400 font-medium">Already registered?</span>
            <div className="flex-1 h-px bg-slate-200/60" />
          </div>

          <button type="button" onClick={() => navigate("/school/signin")}
            className="w-full py-3.5 px-4 bg-white/50 hover:bg-white/80 border border-slate-200/70 rounded-xl text-slate-700 font-semibold text-sm transition-all text-center">
            Sign In Instead
          </button>
        </div>
      </div>
    </main>
  );
}
